import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { useMarkGuideSeen } from "../api/queries";
import { useAuthStore } from "../stores/authStore";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { LogoMark } from "./LogoMark";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { Dialog } from "./Dialog";
import { FeedbackDialog } from "./FeedbackDialog";
import { WelcomeGuide } from "./WelcomeGuide";
import { AnnouncementDialog } from "./AnnouncementDialog";
import { InviteDialog } from "./InviteDialog";
import { RoomWidget } from "./RoomWidget";
import { UserMenu } from "./UserMenu";
import { ADMIN_URL } from "../lib/links";
import { VerificationBanner } from "./VerificationBanner";

/** Общий каркас: шапка с навигацией и футер. */
export function Layout() {
  const { user, status, restore } = useAuthStore();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [goingToAdmin, setGoingToAdmin] = useState(false);
  const [reporting, setReporting] = useState(false);
  // Инструкция открывается сама один раз после регистрации и по кнопке —
  // к справке возвращаются.
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideShown, setGuideShown] = useState(false);
  const markGuideSeen = useMarkGuideSeen();

  // Учётная запись, ждущая удаления, видит только окно «отменить или выйти»:
  // сервер с её токеном почти ничего не разрешает, и фоновые части шапки
  // сыпали бы отказами.
  const active = status === "authenticated" && !user?.deletionScheduledAt;

  if (active && user && !user.guideSeen && !guideShown) {
    setGuideShown(true);
    setGuideOpen(true);
  }

  const closeGuide = () => {
    setGuideOpen(false);
    // Отметка ставится при закрытии, а не при показе: закрытая вкладка
    // не должна считаться прочитанной инструкцией.
    if (user && !user.guideSeen) {
      markGuideSeen.mutate(undefined, { onSuccess: () => void restore() });
    }
  };

  return (
    <div className="flex min-h-full flex-col bg-vintage-grain">
      <header className="sticky top-0 z-30 border-b border-ink-800/80 bg-ink-950/85 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-3 sm:h-16 sm:gap-6 sm:px-4">
          <NavLink to="/" className="flex items-center gap-2 sm:gap-2.5">
            <LogoMark />
            {/* На экранах уже 360 px (Galaxy S9, Fold в сложенном виде) слово
                целиком не оставляет места кнопкам справа, и строка вылезала
                за край с горизонтальной прокруткой. Там остаётся только знак:
                он и так ведёт на главную. */}
            <span className="font-sans text-lg font-semibold tracking-tight text-ink-50 max-[359px]:hidden sm:text-xl">
              Chrono<span className="text-amber-400">Guessr</span>
            </span>
          </NavLink>

          <nav className="hidden items-center gap-1 md:flex">
            <NavItem to="/quests">{t("nav.quests")}</NavItem>
            {/* Лидерборд — для вошедших: гостю пункт только уводил бы на вход. */}
            {status === "authenticated" && <NavItem to="/leaderboard">{t("nav.leaderboard")}</NavItem>}
            <NavItem to="/about">{t("nav.about")}</NavItem>

            {/* «Создать» стоит после разделов и выглядит иначе: это не ещё
                один раздел для чтения, а действие — и единственное место,
                откуда игрок приносит в игру своё. */}
            {status === "authenticated" && (
              <NavLink
                to="/create"
                className={({ isActive }) =>
                  `ml-2 inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-2
                   text-sm font-medium transition-colors ${
                     isActive
                       ? "border-amber-400/60 bg-amber-400/15 text-amber-200"
                       : "border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
                   }`
                }
              >
                <PlusIcon />
                {t("nav.create")}
              </NavLink>
            )}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
            {/* Комната живёт дольше партии, поэтому её место в шапке: она
                нужна и в каталоге, где хозяин выбирает следующий квест, и
                везде, откуда участник может уйти между играми. */}
            {active && <RoomWidget />}

            {status === "loading" && <div className="h-8 w-24 animate-pulse rounded-lg bg-ink-800" />}

            {/* У гостя нет аватарки, а значит и меню под ней: тема и язык
                нужны ему прямо в строке — язык он выбирает ещё до того,
                как прочтёт форму входа. */}
            {status === "anonymous" && (
              <>
                <ThemeSwitcher />
                <LanguageSwitcher />
                <NavLink to="/login" className="btn-ghost">
                  {t("nav.login")}
                </NavLink>
                {/* Регистрация на телефоне не помещается рядом со входом, а с
                    формы входа до неё один клик. */}
                <NavLink to="/register" className="btn-primary hidden sm:inline-flex">
                  {t("nav.register")}
                </NavLink>
              </>
            )}

            {/* Всё, что про самого игрока, — под аватаркой. В строке остаётся
                комната: она про игру с другими и нужна на виду. */}
            {status === "authenticated" && (
              <UserMenu
                onOpenGuide={() => setGuideOpen(true)}
                onOpenFeedback={() => setReporting(true)}
                onOpenAdmin={() => setGoingToAdmin(true)}
              />
            )}

            <MobileMenu authenticated={status === "authenticated"} />
          </div>
        </div>
      </header>

      {guideOpen && <WelcomeGuide onClose={closeGuide} />}

      {/* Анонс ждёт, пока новичок дочитает инструкцию: два окна разом никто
          не читает. Приглашение — поверх всего: зовут играть сейчас. */}
      {active && <AnnouncementDialog enabled={!guideOpen} />}
      <InviteDialog />

      {reporting && <FeedbackDialog onClose={() => setReporting(false)} />}

      {goingToAdmin && (
        <Dialog title={t("admin.goTitle")} onClose={() => setGoingToAdmin(false)}>
          <p className="text-sm text-ink-300">{t("admin.goQuestion")}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={ADMIN_URL} className="btn-primary" onClick={() => setGoingToAdmin(false)}>
              {t("admin.goConfirm")}
            </a>
            <button type="button" className="btn-ghost" onClick={() => setGoingToAdmin(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </Dialog>
      )}

      <VerificationBanner />

      {/* key по адресу перезапускает появление: без него React переиспользует
          узел, и переход между страницами остаётся мгновенным.

          Экран раунда из анимации исключён: пока она идёт, main остаётся
          точкой отсчёта для position: fixed, и карта успевает измерить себя
          по колонке вместо окна — дальше она так и живёт с неверным
          размером. */}
      <main
        key={pathname}
        className={`mx-auto w-full max-w-6xl flex-1 px-3 py-5 sm:px-4 sm:py-8 ${
          pathname.startsWith("/play/") ? "" : "page-enter"
        }`}
      >
        <Outlet />
      </main>

      <footer className="border-t border-ink-800/80 py-5 sm:py-6">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-2 px-3 text-xs text-ink-500 sm:gap-3 sm:px-4">
          <span>{t("footer.tagline")}</span>
          <span className="flex gap-4">
            <NavLink to="/about" className="hover:text-ink-300">
              {t("footer.about")}
            </NavLink>
            <NavLink to="/privacy" className="hover:text-ink-300">
              {t("footer.privacy")}
            </NavLink>
          </span>
        </div>
      </footer>
    </div>
  );
}

/**
 * Навигация на телефоне.
 *
 * Разделы в шапке скрыты уже с планшета, и на телефоне до каталога, рейтинга
 * и создания квеста нельзя было добраться вообще — только набрать адрес
 * руками. Ряд ссылок туда не помещается, поэтому они спрятаны за кнопкой.
 *
 * Здесь только разделы. Профиль, выход и настройки живут под аватаркой,
 * которая видна и на телефоне: дублировать их — значит заставлять гадать,
 * какое из двух меню открыть.
 */
function MobileMenu({ authenticated }: { authenticated: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  // Меню закрывает сам переход, а не эффект на смену адреса: разметка
  // остаётся той же, и открытая панель иначе повисала бы поверх новой
  // страницы.
  const close = () => setOpen(false);

  return (
    <div className="relative md:hidden" ref={box}>
      <button
        type="button"
        className="btn-ghost px-2.5"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("nav.menu")}
      >
        <MenuIcon />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-1 min-w-[11rem] overflow-hidden rounded-xl
                     border border-ink-700 bg-ink-900 py-1 shadow-panel"
        >
          <MenuLink to="/quests" onNavigate={close}>{t("nav.quests")}</MenuLink>
          {authenticated && <MenuLink to="/leaderboard" onNavigate={close}>{t("nav.leaderboard")}</MenuLink>}
          {authenticated && <MenuLink to="/create" onNavigate={close}>{t("nav.create")}</MenuLink>}
          <MenuLink to="/about" onNavigate={close}>{t("nav.about")}</MenuLink>
          {!authenticated && <MenuLink to="/register" onNavigate={close}>{t("nav.register")}</MenuLink>}
        </div>
      )}
    </div>
  );
}

function MenuLink({
  to,
  children,
  onNavigate,
}: {
  to: string;
  children: React.ReactNode;
  onNavigate: () => void;
}) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      onClick={onNavigate}
      className={({ isActive }) =>
        `block px-3 py-2.5 text-sm transition-colors ${
          isActive ? "text-amber-300" : "text-ink-200 hover:bg-ink-850"
        }`
      }
    >
      {children}
    </NavLink>
  );
}


function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  );
}

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          isActive ? "bg-ink-800 text-amber-300" : "text-ink-300 hover:bg-ink-850 hover:text-ink-100"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
